import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface PollOption {
  id: string;
  label: string;
}

export interface Poll extends NostrEvent {
  kind: 1068;
  question: string;
  options: PollOption[];
  pollType: 'singlechoice' | 'multiplechoice';
  endsAt?: number;
  relays: string[];
}

function validatePollEvent(event: NostrEvent): Poll | null {
  if (event.kind !== 1068) return null;

  const options: PollOption[] = event.tags
    .filter(([name]) => name === 'option')
    .map(([_, id, label]) => ({ id, label }))
    .filter(opt => opt.id && opt.label);

  if (options.length < 2) return null;

  const relays = event.tags
    .filter(([name]) => name === 'relay')
    .map(([_, url]) => url)
    .filter(Boolean);

  const pollTypeTag = event.tags.find(([name]) => name === 'polltype')?.[1];
  const pollType = (pollTypeTag === 'multiplechoice' ? 'multiplechoice' : 'singlechoice') as 'singlechoice' | 'multiplechoice';

  const endsAtTag = event.tags.find(([name]) => name === 'endsAt')?.[1];
  const endsAt = endsAtTag ? parseInt(endsAtTag) : undefined;

  return {
    ...event,
    kind: 1068,
    question: event.content,
    options,
    pollType,
    endsAt,
    relays
  };
}

export function usePolls(limit = 20) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['polls', limit],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const events = await nostr.query([{ kinds: [1068], limit }], { signal });
      
      return events
        .map(validatePollEvent)
        .filter((poll): poll is Poll => poll !== null)
        .sort((a, b) => b.created_at - a.created_at);
    },
  });
}

export function usePoll(pollId: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['poll', pollId],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const events = await nostr.query([{ kinds: [1068], ids: [pollId] }], { signal });
      
      if (events.length === 0) return null;
      return validatePollEvent(events[0]);
    },
    enabled: !!pollId,
  });
}
